/**
 * Legal → Statements (#23/#24, Josh 09/28).
 *   Build     pick a period (last month by default, any range on demand) → Preview (nothing saved)
 *             or Generate drafts (one per syndicator with legal activity).
 *   Review    drafts/sent/paid list → PDF (statement + itemized lawyer-charge invoice),
 *             Send (emails the PDF — only on your click), Mark paid (records the payout), Void.
 *   Ledger    paid statements by year / month / week / day, per syndicator or all.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';
import { FileText, Send, CheckCircle2, Ban, Eye, Loader2, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { formatCurrency, formatDate } from '../../lib/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/legal/statements`;
const STATUS_BADGE = { draft: 'outline', sent: 'default', paid: 'secondary', void: 'destructive' };

const pad = (n) => String(n).padStart(2, '0');
const lastMonth = () => {
  const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1);
  const y = d.getFullYear(), m = d.getMonth() + 1;
  const last = new Date(y, m, 0).getDate();
  return { from: `${y}-${pad(m)}-01`, to: `${y}-${pad(m)}-${pad(last)}` };
};
const thisMonth = () => {
  const d = new Date(); const y = d.getFullYear(), m = d.getMonth() + 1;
  return { from: `${y}-${pad(m)}-01`, to: `${y}-${pad(m)}-${pad(new Date(y, m, 0).getDate())}` };
};
const isFullMonth = ({ from, to }) => from.endsWith('-01') && to.slice(0, 7) === from.slice(0, 7)
  && Number(to.slice(8)) === new Date(Number(to.slice(0, 4)), Number(to.slice(5, 7)), 0).getDate();
const netColor = (n) => (n > 0 ? 'text-green-600' : n < 0 ? 'text-red-600' : '');

const StatementsTab = ({ getAuthHeaders }) => {
  const [period, setPeriod] = useState(lastMonth());
  const [syndicators, setSyndicators] = useState([]);
  const [synFilter, setSynFilter] = useState('__all__');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');
  const [view, setView] = useState('statements');   // statements | ledger
  const [dlg, setDlg] = useState(null);             // {kind:'send'|'paid'|'detail', st}

  const headers = useCallback(() => ({ headers: getAuthHeaders() }), [getAuthHeaders]);

  const loadList = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (statusFilter !== 'active') p.set('status', statusFilter);
      if (synFilter !== '__all__') p.set('syndicator_id', synFilter);
      const r = await axios.get(`${API}?${p}`, headers());
      setList(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load statements');
    }
  }, [headers, statusFilter, synFilter]);

  useEffect(() => {
    axios.get(`${API}/syndicators`, headers()).then(r => setSyndicators(r.data)).catch(() => {});
  }, [headers]);
  useEffect(() => { loadList(); }, [loadList]);

  const runPreview = async () => {
    setBusy(true);
    try {
      const p = new URLSearchParams({ from_date: period.from, to_date: period.to });
      if (synFilter !== '__all__') p.set('syndicator_id', synFilter);
      const r = await axios.get(`${API}/preview?${p}`, headers());
      setPreview(r.data);
      if (!r.data.count) toast.info('No legal activity in that period for the selected syndicator(s)');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally {
      setBusy(false);
    }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/generate`, {
        from_date: period.from, to_date: period.to, kind: isFullMonth(period) ? 'monthly' : 'adhoc',
        syndicator_id: synFilter === '__all__' ? null : synFilter, replace_drafts: true,
      }, headers());
      const sk = r.data.skipped.length;
      toast.success(`${r.data.created} draft(s) created, ${r.data.replaced} refreshed${sk ? `, ${sk} already sent/paid (untouched)` : ''}`);
      setPreview(null);
      loadList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generate failed');
    } finally {
      setBusy(false);
    }
  };

  const openPdf = async (st) => {
    try {
      const r = await axios.get(`${API}/${st.id}/pdf`, { ...headers(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Could not build the PDF');
    }
  };

  const voidIt = async (st) => {
    if (!window.confirm(`Void statement ${st.number} for ${st.syndicator_name}?`)) return;
    try {
      await axios.post(`${API}/${st.id}/void`, {}, headers());
      toast.success('Statement voided'); loadList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Void failed');
    }
  };

  return (
    <div className="space-y-5">
      {/* build */}
      <Card>
        <CardHeader className="py-3"><CardTitle className="text-base">Build statements</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="flex gap-1">
            <Button size="sm" variant="outline" onClick={() => setPeriod(lastMonth())}>Last month</Button>
            <Button size="sm" variant="outline" onClick={() => setPeriod(thisMonth())}>This month</Button>
          </div>
          <div><Label className="text-[11px] text-muted-foreground">From</Label><Input type="date" value={period.from} onChange={e => setPeriod(p => ({ ...p, from: e.target.value }))} className="w-[150px]" /></div>
          <div><Label className="text-[11px] text-muted-foreground">To</Label><Input type="date" value={period.to} onChange={e => setPeriod(p => ({ ...p, to: e.target.value }))} className="w-[150px]" /></div>
          <div>
            <Label className="text-[11px] text-muted-foreground">Syndicator</Label>
            <Select value={synFilter} onValueChange={setSynFilter}>
              <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All syndicators</SelectItem>
                {syndicators.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" variant="outline" onClick={runPreview} disabled={busy} data-testid="stmt-preview-btn"><Eye className="h-4 w-4 mr-1" />Preview</Button>
          <Button size="sm" onClick={generate} disabled={busy} data-testid="stmt-generate-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 mr-1" />}Generate drafts</Button>
          <p className="text-[11px] text-muted-foreground basis-full">
            Nothing is built automatically: after the lawyer's remittance is uploaded (reminder around the 7th), click Generate for last month. A full calendar month is saved as the monthly statement; anything else is ad-hoc. Drafts for a period are refreshed on Generate; sent or paid statements are never changed.
          </p>
        </CardContent>
      </Card>

      {preview && <PreviewBlock preview={preview} onClose={() => setPreview(null)} />}

      {/* review / ledger */}
      <div className="flex items-center gap-2">
        <Button size="sm" variant={view === 'statements' ? 'default' : 'outline'} onClick={() => setView('statements')}>Statements</Button>
        <Button size="sm" variant={view === 'ledger' ? 'default' : 'outline'} onClick={() => setView('ledger')}>Payout ledger</Button>
        {view === 'statements' && (
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px] h-8 ml-2"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Draft + sent + paid</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="void">Void</SelectItem>
            </SelectContent>
          </Select>
        )}
        <div className="flex-1" />
        <Button size="sm" variant="ghost" onClick={loadList}><RefreshCw className="h-4 w-4" /></Button>
      </div>

      {view === 'statements' && (
        <Card><CardContent className="pt-4">
          {!list && <p className="text-sm text-muted-foreground">Loading…</p>}
          {list && list.count === 0 && <p className="text-sm text-muted-foreground">No statements yet — build some above.</p>}
          {list && list.count > 0 && (
            <>
              <div className="text-xs text-muted-foreground mb-2">{list.totals.draft} draft · {list.totals.sent} sent · {list.totals.paid} paid · net {formatCurrency(list.totals.net)}</div>
              <Table>
                <TableHeader><TableRow className="text-xs">
                  <TableHead>Statement</TableHead><TableHead>Syndicator</TableHead><TableHead>Period</TableHead>
                  <TableHead className="text-right">Recoveries share</TableHead><TableHead className="text-right">Charges share</TableHead>
                  <TableHead className="text-right">Net</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {list.statements.map(st => (
                    <TableRow key={st.id} data-testid={`stmt-row-${st.number}`}>
                      <TableCell className="text-xs"><div className="font-medium">{st.number}</div><div className="text-muted-foreground">{st.kind}</div></TableCell>
                      <TableCell className="text-xs">{st.syndicator_name}<div className="text-muted-foreground">{st.syndicator_email || 'no email on contact'}</div></TableCell>
                      <TableCell className="text-xs">{formatDate(st.period_from)} – {formatDate(st.period_to)}</TableCell>
                      <TableCell className="text-right text-xs">{formatCurrency(st.totals.recovery_share)}</TableCell>
                      <TableCell className="text-right text-xs text-red-700">−{formatCurrency(st.totals.charge_share)}</TableCell>
                      <TableCell className={`text-right font-semibold ${netColor(st.totals.net)}`}>{formatCurrency(st.totals.net)}</TableCell>
                      <TableCell>
                        <Badge variant={STATUS_BADGE[st.status] || 'outline'}>{st.status}</Badge>
                        {st.status === 'sent' && st.sent_at && <div className="text-[10px] text-muted-foreground">{formatDate(st.sent_at)} → {st.sent_to}</div>}
                        {st.status === 'paid' && <div className="text-[10px] text-muted-foreground">{formatDate(st.paid_at)} {st.paid_method} {st.paid_reference}</div>}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setDlg({ kind: 'detail', st })}>Detail</Button>
                          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => openPdf(st)}><FileText className="h-3 w-3 mr-1" />PDF</Button>
                          {st.status !== 'void' && <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setDlg({ kind: 'send', st })}><Send className="h-3 w-3 mr-1" />{st.status === 'sent' || st.sent_at ? 'Resend' : 'Send'}</Button>}
                          {(st.status === 'draft' || st.status === 'sent') && <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-green-700" onClick={() => setDlg({ kind: 'paid', st })}><CheckCircle2 className="h-3 w-3 mr-1" />Mark paid</Button>}
                          {(st.status === 'draft' || st.status === 'sent') && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-red-700" onClick={() => voidIt(st)} title="Void"><Ban className="h-3 w-3" /></Button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent></Card>
      )}

      {view === 'ledger' && <LedgerView headers={headers} syndicators={syndicators} />}

      {dlg?.kind === 'send' && <SendDialog st={dlg.st} headers={headers} onClose={() => setDlg(null)} onDone={loadList} />}
      {dlg?.kind === 'paid' && <MarkPaidDialog st={dlg.st} headers={headers} onClose={() => setDlg(null)} onDone={loadList} />}
      {dlg?.kind === 'detail' && <DetailDialog st={dlg.st} headers={headers} onClose={() => setDlg(null)} />}
    </div>
  );
};

// ─── preview (unsaved) ───────────────────────────────────────────────────────
const PreviewBlock = ({ preview, onClose }) => {
  const [open, setOpen] = useState({});
  return (
    <Card className="border-indigo-200">
      <CardHeader className="py-3 flex-row items-center justify-between">
        <CardTitle className="text-base">Preview — {formatDate(preview.period_from)} to {formatDate(preview.period_to)} · {preview.count} statement(s) · net {formatCurrency(preview.total_net)}</CardTitle>
        <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {preview.pfs_retained && (
          <div className="text-xs text-gray-600 bg-gray-50 border rounded px-3 py-2" data-testid="pfs-retained-line">
            PFS retained (own positions, no statement): <span className="font-semibold tabular-nums">{formatCurrency(preview.pfs_retained.net)}</span>
            {' '}on {preview.pfs_retained.deals} deal(s) — recoveries {formatCurrency(preview.pfs_retained.recovery_share)}, charges {formatCurrency(preview.pfs_retained.charge_share)}
          </div>
        )}
        {preview.statements.map(st => (
          <div key={st.syndicator_id} className="border rounded p-2">
            <button className="w-full flex items-center justify-between text-sm" onClick={() => setOpen(o => ({ ...o, [st.syndicator_id]: !o[st.syndicator_id] }))}>
              <span className="flex items-center gap-2">{open[st.syndicator_id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}<b>{st.syndicator_name}</b> <span className="text-muted-foreground text-xs">{st.totals.deals} deal(s)</span></span>
              <span className="text-xs">share {formatCurrency(st.totals.recovery_share)} · charges −{formatCurrency(st.totals.charge_share)} · <b className={netColor(st.totals.net)}>net {formatCurrency(st.totals.net)}</b></span>
            </button>
            {open[st.syndicator_id] && <StatementDetail st={st} />}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

const StatementDetail = ({ st }) => (
  <div className="mt-2 space-y-3 text-xs">
    <Table>
      <TableHeader><TableRow><TableHead>Deal</TableHead><TableHead>%</TableHead><TableHead className="text-right">Lawyer rec.</TableHead><TableHead className="text-right">In-house rec.</TableHead><TableHead className="text-right">Your share</TableHead><TableHead className="text-right">Charges</TableHead><TableHead className="text-right">Charge share</TableHead><TableHead className="text-right">Net</TableHead></TableRow></TableHeader>
      <TableBody>
        {st.deals.map(l => (
          <TableRow key={l.deal_uuid}>
            <TableCell>#{l.deal_id} <span className="text-muted-foreground">{l.deal_name}</span></TableCell>
            <TableCell>{l.ownership_percentage}%</TableCell>
            <TableCell className="text-right">{formatCurrency(l.lawyer_recovery_gross)}</TableCell>
            <TableCell className="text-right">{formatCurrency(l.in_house_recovery_gross)}</TableCell>
            <TableCell className="text-right">{formatCurrency(l.recovery_share)}</TableCell>
            <TableCell className="text-right">{formatCurrency(l.charge_total)}</TableCell>
            <TableCell className="text-right text-red-700">−{formatCurrency(l.charge_share)}</TableCell>
            <TableCell className={`text-right font-medium ${netColor(l.net)}`}>{formatCurrency(l.net)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    {st.invoice.length > 0 && (
      <div>
        <p className="font-medium mb-1">Invoice — lawyer charges</p>
        <Table>
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Deal</TableHead><TableHead>Charge</TableHead><TableHead>Description</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>%</TableHead><TableHead className="text-right">Their share</TableHead></TableRow></TableHeader>
          <TableBody>
            {st.invoice.map((c, i) => (
              <TableRow key={i}><TableCell>{formatDate(c.date)}</TableCell><TableCell>#{c.deal_id}</TableCell><TableCell>{c.type === 'LAWYER_FEE' ? 'Contingency fee' : 'Legal cost'}</TableCell><TableCell className="max-w-[260px] truncate" title={c.description}>{c.description}</TableCell><TableCell className="text-right">{formatCurrency(c.amount)}</TableCell><TableCell>{c.ownership_percentage}%</TableCell><TableCell className="text-right">{formatCurrency(c.share)}</TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    )}
  </div>
);

const DetailDialog = ({ st, headers, onClose }) => {
  const [full, setFull] = useState(null);
  useEffect(() => { axios.get(`${API}/${st.id}`, headers()).then(r => setFull(r.data)).catch(() => toast.error('Could not load')); }, [st.id, headers]);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl">
        <DialogHeader><DialogTitle>{st.number} — {st.syndicator_name} · {formatDate(st.period_from)} to {formatDate(st.period_to)}</DialogTitle></DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto">{full ? <StatementDetail st={full} /> : <p className="text-sm">Loading…</p>}</div>
      </DialogContent>
    </Dialog>
  );
};

// ─── send / mark paid ────────────────────────────────────────────────────────
const SendDialog = ({ st, headers, onClose, onDone }) => {
  const [to, setTo] = useState(st.syndicator_email || '');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!to.trim()) { toast.error('Enter an email address'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/${st.id}/send`, { to_email: to.trim(), message }, headers());
      toast.success(`Sent to ${r.data.sent_to}`); onDone(); onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Send {st.number} to {st.syndicator_name}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Emails the PDF (statement + invoice). Net {formatCurrency(st.totals.net)}. Check the PDF first if you haven't.</p>
        <div><Label className="text-xs">To</Label><Input type="email" value={to} onChange={e => setTo(e.target.value)} /></div>
        <div><Label className="text-xs">Note (optional, goes above the standard text)</Label><Textarea rows={3} value={message} onChange={e => setMessage(e.target.value)} /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={send} disabled={busy}><Send className="h-4 w-4 mr-1" />{busy ? 'Sending…' : 'Send'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const MarkPaidDialog = ({ st, headers, onClose, onDone }) => {
  const [form, setForm] = useState({ paid_at: new Date().toISOString().slice(0, 10), method: 'ACH', reference: '', amount: String(st.totals.net) });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/${st.id}/mark-paid`, { paid_at: form.paid_at, method: form.method, reference: form.reference, amount: parseFloat(form.amount) }, headers());
      toast.success('Payout recorded'); onDone(); onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not record');
    } finally {
      setBusy(false);
    }
  };
  const owed = st.totals.net < 0;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Record {owed ? 'receipt' : 'payout'} — {st.number}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">{owed ? `${st.syndicator_name} owes ${formatCurrency(-st.totals.net)} (charges exceeded recoveries).` : `Paying ${st.syndicator_name} ${formatCurrency(st.totals.net)}.`} This records the statement as paid; it does not post to the deal ledger.</p>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Date</Label><Input type="date" value={form.paid_at} onChange={e => setForm(f => ({ ...f, paid_at: e.target.value }))} /></div>
          <div><Label className="text-xs">Amount</Label><Input type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} /></div>
          <div>
            <Label className="text-xs">Method</Label>
            <Select value={form.method} onValueChange={v => setForm(f => ({ ...f, method: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{['ACH', 'Wire', 'Check', 'Offset against distribution', 'Other'].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Reference</Label><Input value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} placeholder="trace / check #" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy}><CheckCircle2 className="h-4 w-4 mr-1" />{busy ? 'Saving…' : 'Mark paid'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── ledger ──────────────────────────────────────────────────────────────────
const LedgerView = ({ headers, syndicators }) => {
  const [group, setGroup] = useState('month');
  const [basis, setBasis] = useState('paid');
  const [syn, setSyn] = useState('__all__');
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  useEffect(() => {
    const p = new URLSearchParams({ group, basis });
    if (syn !== '__all__') p.set('syndicator_id', syn);
    axios.get(`${API}/ledger?${p}`, headers()).then(r => setData(r.data)).catch(() => toast.error('Could not load ledger'));
  }, [group, basis, syn, headers]);
  return (
    <Card>
      <CardHeader className="py-3">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-base mr-2">Payout ledger</CardTitle>
          {['year', 'month', 'week', 'day'].map(g => <Button key={g} size="sm" variant={group === g ? 'default' : 'outline'} onClick={() => setGroup(g)}>{g}</Button>)}
          <Select value={basis} onValueChange={setBasis}>
            <SelectTrigger className="w-[170px] h-8"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="paid">by payout date</SelectItem><SelectItem value="period">by statement period</SelectItem></SelectContent>
          </Select>
          <Select value={syn} onValueChange={setSyn}>
            <SelectTrigger className="w-[200px] h-8"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="__all__">All syndicators</SelectItem>{syndicators.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {!data && <p className="text-sm text-muted-foreground">Loading…</p>}
        {data && data.buckets.length === 0 && <p className="text-sm text-muted-foreground">No paid statements yet.</p>}
        {data && data.buckets.length > 0 && (
          <>
            <div className="text-xs text-muted-foreground mb-2">{data.totals.statements} paid statement(s) · net {formatCurrency(data.totals.net)} · paid {formatCurrency(data.totals.paid)}</div>
            <Table>
              <TableHeader><TableRow className="text-xs"><TableHead>{group}</TableHead><TableHead className="text-right">Statements</TableHead><TableHead className="text-right">Recoveries share</TableHead><TableHead className="text-right">Charges share</TableHead><TableHead className="text-right">Net</TableHead><TableHead className="text-right">Paid</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.buckets.map(b => (
                  <React.Fragment key={b.period}>
                    <TableRow className="cursor-pointer" onClick={() => setOpen(o => ({ ...o, [b.period]: !o[b.period] }))}>
                      <TableCell className="font-medium">{open[b.period] ? <ChevronDown className="inline h-3 w-3 mr-1" /> : <ChevronRight className="inline h-3 w-3 mr-1" />}{b.period}</TableCell>
                      <TableCell className="text-right">{b.statements}</TableCell>
                      <TableCell className="text-right">{formatCurrency(b.recovery_share)}</TableCell>
                      <TableCell className="text-right text-red-700">−{formatCurrency(b.charge_share)}</TableCell>
                      <TableCell className={`text-right font-semibold ${netColor(b.net)}`}>{formatCurrency(b.net)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(b.paid)}</TableCell>
                    </TableRow>
                    {open[b.period] && b.items.map(it => (
                      <TableRow key={it.id} className="bg-muted/30 text-xs">
                        <TableCell className="pl-8">{it.syndicator_name} · {formatDate(it.period_from)}–{formatDate(it.period_to)}</TableCell>
                        <TableCell></TableCell><TableCell></TableCell><TableCell></TableCell>
                        <TableCell className={`text-right ${netColor(it.net)}`}>{formatCurrency(it.net)}</TableCell>
                        <TableCell className="text-right">{formatDate(it.paid_at)} {it.paid_method} {it.paid_reference}</TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default StatementsTab;
